import { BlockCodePart } from "@redotech/redo-block-service-common/block-content";
import { BlockServiceTheme } from "@redotech/redo-block-service-common/messages";
import {
  BlockServiceHandle,
  mountBlockFrame,
} from "@redotech/redo-block-service-parent/mount";
import { memo, useEffect, useRef } from "react";

export interface BlockServiceFrameError {
  kind: string;
  message?: string;
  url?: string;
}

export interface BlockServiceFrameProps {
  /** Identifies the block within the host page; also used as the iframe title. */
  blockId: string;
  html: string;
  code?: BlockCodePart[];
  /** Posted to the frame as `redo:context`; re-posted whenever its JSON value changes. */
  context: unknown;
  /** Forwarded to the frame alongside `context`; the host's own placement metadata. */
  page: unknown;
  theme?: BlockServiceTheme;
  /** Sanitizes `html` for the no-`redo:ready` fallback; the host owns the allowlist. */
  sanitizeFallback: (html: string) => string;
  /** Class name for the no-`redo:ready` fallback node, alongside `block-service-fallback`. */
  fallbackClassName?: string;
  onHeight?: (height: number) => void;
  /** The host decides whether/how to surface an error to its viewer. */
  onError?: (error: BlockServiceFrameError) => void;
  /** Called right after a fresh frame mounts, e.g. to clear stale error state. */
  onMount?: () => void;
}

/** Generic host for a block-service iframe, usable by any Redo page. */
export const BlockServiceFrame = memo(function BlockServiceFrame(
  props: BlockServiceFrameProps,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<BlockServiceHandle | null>(null);
  const lastPostedContextKeyRef = useRef<string | null>(null);

  const code = props.code ?? [];
  // Keyed by value, not identity, so an unrelated re-render doesn't tear down
  // the iframe. `page` has no post-mount update path, unlike `context`.
  const mountKey = JSON.stringify({
    html: props.html,
    code,
    theme: props.theme,
    page: props.page,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const handle = mountBlockFrame({
      container,
      blockId: props.blockId,
      html: props.html,
      code,
      theme: props.theme ?? {},
      context: props.context,
      page: props.page,
      sanitizeFallback: props.sanitizeFallback,
      fallbackClassName: props.fallbackClassName,
      onHeight: props.onHeight,
      onError: props.onError,
    });
    props.onMount?.();
    lastPostedContextKeyRef.current = JSON.stringify(props.context);
    handleRef.current = handle;
    return () => {
      handle.destroy();
      handleRef.current = null;
    };
    // Real context changes for the live iframe go through `updateContext`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.blockId, mountKey]);

  useEffect(() => {
    const key = JSON.stringify(props.context);
    if (lastPostedContextKeyRef.current === key) {
      return;
    }
    lastPostedContextKeyRef.current = key;
    handleRef.current?.updateContext(props.context);
  }, [props.context]);

  return <div ref={containerRef} />;
});
